'use client'

import { createContext, useContext, useEffect, useState, useSyncExternalStore } from 'react'
import { usePostHog } from '@posthog/next'
import {
  type AnalyticsConsent,
  autoAcceptIfUndecided,
  CONSENT_CHANGE_EVENT,
  getCachedRequiresConsent,
  getStoredConsent,
  resolveCaptureMode,
  setCachedRequiresConsent,
  setStoredConsent,
} from '@/lib/analytics-consent'

interface AnalyticsConsentContextValue {
  consent: AnalyticsConsent | null
  showBanner: boolean
  accept(): void
  reject(): void
  openPreferences(): void
}

const AnalyticsConsentContext = createContext<AnalyticsConsentContextValue | null>(null)

export function useAnalyticsConsent(): AnalyticsConsentContextValue {
  const ctx = useContext(AnalyticsConsentContext)
  if (!ctx) throw new Error('useAnalyticsConsent must be used inside AnalyticsConsentProvider')
  return ctx
}

// Read the stored decision via an external store so server + first hydration
// render agree (both read consent as null → banner shown), then the client
// snapshot takes over post-hydration. Avoids a hydration mismatch under
// cacheComponents.
const listeners = new Set<() => void>()

function subscribe(callback: () => void): () => void {
  listeners.add(callback)
  return () => {
    listeners.delete(callback)
  }
}

function emitChange(): void {
  for (const listener of listeners) listener()
}

function getServerSnapshot(): AnalyticsConsent | null {
  return null
}

interface Props {
  children: React.ReactNode
}

export function AnalyticsConsentProvider({ children }: Props) {
  const posthog = usePostHog()
  const consent = useSyncExternalStore(subscribe, getStoredConsent, getServerSnapshot)
  const [forceOpen, setForceOpen] = useState(false)
  // Whether the visitor's region (EU/EEA/UK) requires an opt-in banner. Null
  // until resolved. Outside those regions we enable analytics by default, so no
  // banner is shown — but the footer "Cookie preferences" link can still open it
  // to opt out (satisfies opt-out regimes like CCPA).
  const [requiresConsent, setRequiresConsent] = useState<boolean | null>(null)

  const showBanner = forceOpen || (consent === null && requiresConsent === true)

  // Applies the decision to PostHog on mount and on every change. The three-way branch lives
  // in `resolveCaptureMode` (src/lib/analytics-consent.ts) so it is unit-testable without a
  // DOM — its docblock explains why `opt_out` is what *engages* cookieless capture rather than
  // stopping it, and why an undecided visitor must wait for the region to resolve.
  // `__tests__/analytics-consent.test.ts` covers the full matrix.
  useEffect(() => {
    if (!posthog) return

    const mode = resolveCaptureMode(consent, requiresConsent)
    if (mode === 'opt_in') {
      posthog.opt_in_capturing()
      posthog.startSessionRecording()
    } else if (mode === 'opt_out') {
      posthog.opt_out_capturing()
    }
  }, [posthog, consent, requiresConsent])

  // Determine, for an undecided visitor, whether their region requires opt-in.
  // EU/EEA/UK → show the banner (analytics stays off). Elsewhere → auto-accept
  // so analytics runs without a banner. Cached per browser to avoid refetching.
  useEffect(() => {
    if (consent !== null) return
    let cancelled = false

    async function resolveRegion(): Promise<void> {
      const cached = getCachedRequiresConsent()
      let needsConsent: boolean
      if (cached !== null) {
        needsConsent = cached
      } else {
        try {
          const res = await fetch('/api/geo')
          const data: { requiresConsent?: boolean } = await res.json()
          needsConsent = data.requiresConsent !== false
        } catch {
          needsConsent = true
        }
        setCachedRequiresConsent(needsConsent)
      }
      if (cancelled) return
      if (needsConsent) {
        setRequiresConsent(true)
      } else {
        setRequiresConsent(false)
        if (autoAcceptIfUndecided()) emitChange()
      }
    }

    void resolveRegion()
    return () => {
      cancelled = true
    }
  }, [consent])

  // Let the static footer "Cookie preferences" link reopen the banner.
  useEffect(() => {
    function handleOpenPreferences(): void {
      setForceOpen(true)
    }
    window.addEventListener(CONSENT_CHANGE_EVENT, handleOpenPreferences)
    return () => {
      window.removeEventListener(CONSENT_CHANGE_EVENT, handleOpenPreferences)
    }
  }, [])

  function accept(): void {
    setStoredConsent('accepted')
    emitChange()
    setForceOpen(false)
  }

  function reject(): void {
    setStoredConsent('rejected')
    emitChange()
    setForceOpen(false)
  }

  function openPreferences(): void {
    setForceOpen(true)
  }

  return (
    <AnalyticsConsentContext.Provider
      value={{ consent, showBanner, accept, reject, openPreferences }}
    >
      {children}
    </AnalyticsConsentContext.Provider>
  )
}
